import { AbstractControl, ValidationErrors, ValidatorFn, FormArray } from "@angular/forms";
import numeral from "numeral";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

export function safeParseFloat(value: any) {
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Converts JSON data to Excel and triggers a download.
 *
 * @param jsonData - Array of objects, each representing a row in Excel.
 * @param fileName - The name of the resulting Excel file.
 * @param extension - The file extension of the resulting Excel file.
 * @param options - Options to be applied on the resulting Excel file.
 */
export async function downloadJSONAsExcel<T extends Record<string, any>>(
  jsonData: T[],
  fileName: string,
  extension: string = ".xlsx",
  options: { boldLastRow?: boolean; boldFirstRow?: boolean } = { boldLastRow: true, boldFirstRow: true },
): Promise<void> {
  if (jsonData.length === 0) return;

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Sheet1");

  // Set up columns using keys from the first row
  const firstRow = jsonData[0];
  const columnKeys = Object.keys(firstRow);
  worksheet.columns = columnKeys.map((key) => ({
    header: key,
    key: key,
    width: key.length + 10, // Adjust for readability
  }));

  // Add all rows
  jsonData.forEach((row) => {
    worksheet.addRow(row);
  });

  // Make header row bold
  const headerRow = worksheet.getRow(1);
  if (headerRow) headerRow.font = { bold: options.boldFirstRow };

  // Optional: Apply bold style to last row if it's a "totals" row
  const lastRow = worksheet.lastRow;
  if (lastRow) lastRow.font = { bold: options.boldLastRow };

  // Trigger download
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), `${fileName}${extension}`);
}

export async function downloadTableAsExcel(tableId: string, fileName: string): Promise<void> {
  const tableElement = document.getElementById(tableId) as HTMLTableElement;

  if (!tableElement) {
    console.error("Table element not found");
    return;
  }

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Sheet1");

  const rows: string[][] = [];
  for (const row of Array.from(tableElement.rows)) {
    const rowData: string[] = [];
    for (const cell of Array.from(row.cells)) {
      rowData.push(cell.textContent?.trim() || "");
    }
    rows.push(rowData);
  }

  rows.forEach((row) => {
    worksheet.addRow(row);
  });

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), `${fileName}.xlsx`);
}

export const strongPasswordPattern = '^(?=.*[A-Z])(?=.*[a-z])(?=.*\\d)(?=.*[!@#$%^&*(),.?":{}|<>])[A-Za-z\\d!@#$%^&*(),.?":{}|<>]{8,}$';

function validateQuickBetAmount(value: any): ValidationErrors | null {
  const errors: ValidationErrors = {};

  if (value === null || value === "") {
    errors["required"] = true;
  } else if (typeof value === "string" && !isNumeric(value)) {
    const parsedValue = NumeralUtil.fromCompact(value);
    if (parsedValue === null || parsedValue <= 0) {
      errors["invalidCompactNotation"] = { actualValue: value };
    }
  } else if (value !== null && safeParseFloat(value) <= 0) {
    errors["min"] = { requiredValue: 1, actualValue: value };
  }

  return Object.keys(errors).length > 0 ? errors : null;
}

export function quickBetAmountsValidator(): ValidatorFn {
  return (formArray: AbstractControl): ValidationErrors | null => {
    const errors: ValidationErrors = {};
    const controls = (formArray as FormArray).controls;

    controls.forEach((control) => {
      const controlErrors = validateQuickBetAmount(control.value);
      if (controlErrors) {
        Object.assign(errors, controlErrors);
      }
    });

    return Object.keys(errors).length > 0 ? errors : null;
  };
}

export function quickBetAmountValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    return validateQuickBetAmount(control.value);
  };
}

export class NumeralUtil {
  private static compactPattern = /^[\d]+(\.\d+)?[kMBT]$/i;

  static toCompact(value: number): string {
    if (value == null) return "";
    return numeral(value).format("0.0a");
  }

  static fromCompact(value: string): number | null {
    if (!value || !NumeralUtil.isValidCompactNotation(value)) {
      console.warn(`Invalid compact notation: "${value}"`);
      return null;
    }
    return numeral(value).value();
  }

  static isValidCompactNotation(value: string): boolean {
    return this.compactPattern.test(value);
  }
}

export function isNumeric(value: any): boolean {
  return !isNaN(parseFloat(value)) && isFinite(value);
}

export function parseError(errorString: string) {
  const match = errorString?.match(/ERROR:\s*(.*?)(?:\s+DETAIL:|$)/);
  return match ? match[1].trim() : null;
}

export function loaderItems(length: number): number[] {
  return Array(length)
    .fill(null)
    .map((_, i) => i);
}

export async function downloadJSONAsCSV<T extends Record<string, any>>(jsonData: T[], fileName: string) {
  if (jsonData.length === 0) return;

  const keys = Object.keys(jsonData[0]);

  // Convert JSON to CSV string
  const csvRows = [
    keys.join(","), // header row
    ...jsonData.map((row) =>
      keys
        .map((key) => {
          const cell = row[key] ?? "";
          const escaped = String(cell).replace(/"/g, '""');
          return `"${escaped}"`;
        })
        .join(","),
    ),
  ];

  const csvString = csvRows.join("\n");

  // Create a Blob and trigger the download
  const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
  saveAs(blob, `${fileName}.csv`);
}

export function notNaNValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    return isNaN(Number(control.value)) ? { notNumber: true } : null;
  };
}
